import { useState, type FormEvent } from 'react';
import { ApiError } from '../../../application/ports/api';
import { errorMessage, useT } from '../../i18n/i18n';

type Errors<T> = Partial<Record<keyof T | '_', string>>;

/**
 * Minimal form state: values, client validation, submit with server error mapping.
 * `validate` returns per-field messages; `submit` may throw ApiError with `fields`.
 */
export function useForm<T extends Record<string, string>>(
  initial: T,
  validate: (v: T) => Errors<T>,
  submit: (v: T) => Promise<void>,
  fieldMessage: (field: string, code: string) => string | undefined,
) {
  const t = useT();
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<Errors<T>>({});
  const [submitting, setSubmitting] = useState(false);

  const set = (field: keyof T) => (e: { target: { value: string } }) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const clientErrors = validate(values);
    if (Object.values(clientErrors).some(Boolean)) {
      setErrors(clientErrors);
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      await submit(values);
    } catch (err) {
      const next: Errors<T> = {};
      if (err instanceof ApiError) {
        for (const [f, code] of Object.entries(err.fields)) {
          const msg = fieldMessage(f, code);
          if (msg && f in values) next[f as keyof T] = msg;
        }
      }
      if (Object.keys(next).length === 0) next._ = errorMessage(t, err);
      setErrors(next);
      setSubmitting(false);
    }
  };

  const reset = () => {
    setErrors({});
    setSubmitting(false);
  };

  return { values, errors, submitting, set, onSubmit, reset };
}
